import { useSyncExternalStore } from 'react';
import { formatChord, parseChord } from '../model/chord';
import { platform } from '../model/platform';
import type { KeybindingCommandId, KeyboardLayoutSnapshot, Platform } from '../model/keybindingTypes';
import {
  getEffectiveKeymap,
  getKeybindingLayoutMap,
  subscribeEffectiveKeymap,
} from '../dispatch/effectiveKeymap';

/**
 * One chord as the app shows it; physical tokens carry the layout's character
 * when the map knows one (`Ctrl+ö [Semicolon]`). Shared with the Settings tab,
 * which passes the layout snapshot it renders with.
 */
export function formattedChord(
  chord: string,
  targetPlatform: Platform,
  layout: KeyboardLayoutSnapshot = getKeybindingLayoutMap().getSnapshot(),
): string {
  const parsed = parseChord(chord);
  if (!parsed?.physical) return formatChord(chord, targetPlatform);
  const layoutKey = layout.entries.get(parsed.key);
  const displayKey = layoutKey && Array.from(layoutKey).length === 1 ? layoutKey : undefined;
  return formatChord({ ...parsed, ...(displayKey ? { displayKey } : {}) }, targetPlatform);
}

export function getShortcutLabel(
  commandId: KeybindingCommandId,
  targetPlatform: Platform = platform(),
): string {
  return getEffectiveKeymap()[commandId]
    .map((chord) => formattedChord(chord, targetPlatform))
    .filter(Boolean)
    .join(' / ');
}

export function useShortcutLabel(commandId: KeybindingCommandId): string {
  useSyncExternalStore(
    subscribeEffectiveKeymap,
    getEffectiveKeymap,
    getEffectiveKeymap,
  );
  const layoutMap = getKeybindingLayoutMap();
  useSyncExternalStore(layoutMap.subscribe, layoutMap.getSnapshot, layoutMap.getSnapshot);
  return getShortcutLabel(commandId);
}
